import React, { useCallback, memo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Button,
  Grid,
  TextField,
  Box,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { EpisodeData, LinkData } from "../../context/FileLibraryContext";

interface EpisodeEditorProps {
  seriesIndex: number;
  episodes: EpisodeData[];
  onChange: (newEpisodes: EpisodeData[]) => void;
}

const EpisodeEditor: React.FC<EpisodeEditorProps> = ({ seriesIndex, episodes, onChange }) => {
  const updateEpisodes = useCallback(
    (newList: EpisodeData[]) => onChange(newList),
    [onChange]
  );

  const moveEpisode = useCallback(
    (from: number, to: number) => {
      const list = [...episodes];
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item);
      onChange(list);
    },
    [episodes, onChange]
  );

  const handleField = useCallback(
    <K extends keyof EpisodeData>(idx: number, field: K, value: EpisodeData[K]) => {
      const list = [...episodes];
      list[idx] = { ...list[idx], [field]: value };
      onChange(list);
    },
    [episodes, onChange]
  );

  const addEpisode = useCallback(() => {
    const newEp: EpisodeData = {
      id: Date.now().toString(),
      name: "",
      accessTier: 0,
      type: "",
      episode: episodes.length + 1,
      flavorText: "",
      description: "",
      streamLinks: [],
      downloadLinks: [],
    };
    onChange([...episodes, newEp]);
  }, [episodes, onChange]);

  const deleteEpisode = useCallback(
    (idx: number) => onChange(episodes.filter((_, i) => i !== idx)),
    [episodes, onChange]
  );

  const handleLinkList = useCallback(
    (
      idx: number,
      listKey: 'streamLinks' | 'downloadLinks',
      newList: LinkData[]
    ) => {
      const list = [...episodes];
      list[idx] = { ...list[idx], [listKey]: newList };
      onChange(list);
    },
    [episodes, onChange]
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">Episodes</Typography>
      {episodes.map((ep, idx) => (
        <Accordion key={ep.id} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flexGrow: 1 }}>
              {`${idx + 1} - ${ep.name || "Untitled"}`}
            </Typography>
            <IconButton size="small" disabled={idx === 0} onClick={e => { e.stopPropagation(); moveEpisode(idx, idx - 1); }}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={idx === episodes.length - 1} onClick={e => { e.stopPropagation(); moveEpisode(idx, idx + 1); }}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={e => { e.stopPropagation(); deleteEpisode(idx); }}>
              <DeleteIcon />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Name"
                  fullWidth
                  value={ep.name}
                  onChange={e => handleField(idx, 'name', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="Tier"
                  type="number"
                  fullWidth
                  value={ep.accessTier}
                  onChange={e => handleField(idx, 'accessTier', Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="#"
                  type="number"
                  fullWidth
                  value={ep.episode}
                  onChange={e => handleField(idx, 'episode', Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  label="Type"
                  fullWidth
                  value={ep.type}
                  onChange={e => handleField(idx, 'type', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Flavor Text"
                  fullWidth
                  multiline
                  value={ep.flavorText}
                  onChange={e => handleField(idx, 'flavorText', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  value={ep.description}
                  onChange={e => handleField(idx, 'description', e.target.value)}
                />
              </Grid>

              {/* Stream and Download Links Editors */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Stream Links</Typography>
                {ep.streamLinks.map((link, lIdx) => (
                  <TextField
                    key={lIdx}
                    label={`Link ${lIdx + 1}`}
                    fullWidth
                    value={link.url}
                    onChange={e => {
                      const newLinks = [...ep.streamLinks];
                      newLinks[lIdx] = { ...newLinks[lIdx], url: e.target.value };
                      handleLinkList(idx, 'streamLinks', newLinks);
                    }}
                  />
                ))}
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        const newLinks = [...ep.streamLinks, { url: "", type: "", accessTier: 0, title: "" }];
                        handleLinkList(idx, 'streamLinks', newLinks);
                    }}
                >
                    Add Link
                </Button>


              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
      
      <Button size="small" startIcon={<AddIcon />} onClick={addEpisode} sx={{ mb: 1 }}>
        Add Episode
      </Button>
    </Box>
  );
};

export default memo(EpisodeEditor);
